import { useState, useEffect } from "react";
import { X, Plus, Check, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UpsellPopup({ outfitId, isOpen, onClose, onAddItems, existingBundleIds = [] }) {
  const [suggestions, setSuggestions] = useState([]);
  const [discount, setDiscount] = useState(5);
  const [selectedIds, setSelectedIds] = useState(new Set(existingBundleIds));

  useEffect(() => {
    if (!isOpen || !outfitId) return;
    fetch(`${API}/outfits/${outfitId}/suggestions?limit=4`)
      .then(r => r.json())
      .then(d => {
        setSuggestions((d.suggestions || []).filter(s => !existingBundleIds.includes(s.id)));
        setDiscount(d.bundle_discount_percent || 5);
      })
      .catch(() => {});
  }, [isOpen, outfitId]);

  if (!isOpen) return null;

  const toggleItem = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = () => {
    const items = suggestions.filter(s => selectedIds.has(s.id));
    onAddItems?.(items);
    onClose();
  };

  const newSelections = suggestions.filter(s => selectedIds.has(s.id));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 animate-fadeIn" data-testid="upsell-popup-overlay">
      <div className="bg-white w-[95vw] max-w-lg mx-4 max-h-[85vh] overflow-y-auto" data-testid="upsell-popup">
        <div className="sticky top-0 bg-white border-b border-[#282828]/10 px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
            <h3 className="text-sm text-[#282828] font-medium">Complete Your Look</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#676664]/40 hover:text-[#676664]" data-testid="upsell-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {discount > 0 && (
            <div className="bg-[#9A7B4F]/10 p-3 mb-5 text-center">
              <p className="text-[11px] text-[#9A7B4F] font-medium tracking-[0.1em] uppercase">
                Bundle & Save {discount}%
              </p>
              <p className="text-[10px] text-[#676664]/60 font-light mt-0.5">Add complementary pieces to your booking</p>
            </div>
          )}

          {suggestions.length === 0 ? (
            <p className="text-center text-[13px] text-[#676664]/50 py-6">No additional suggestions available</p>
          ) : (
            <div className="space-y-3">
              {suggestions.map((item) => {
                const isSelected = selectedIds.has(item.id);
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                      isSelected ? "border-[#9A7B4F] bg-[#9A7B4F]/5" : "border-[#282828]/10 hover:border-[#282828]/20"
                    }`}
                    onClick={() => toggleItem(item.id)}
                    data-testid={`upsell-item-${item.id}`}
                  >
                    <div className="w-14 h-18 flex-shrink-0 overflow-hidden bg-[#f7f5f4]">
                      <img src={item.images?.[0]} alt={item.name} className="w-full h-full object-cover" style={{ objectPosition: `${(item.image_focal_points || {})[item.images?.[0]]?.x ?? 50}% ${(item.image_focal_points || {})[item.images?.[0]]?.y ?? 50}%` }} onError={e => { e.target.style.display = "none"; }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] text-[#282828] truncate">{item.name}</p>
                      <p className="text-[11px] text-[#676664] font-light">From ${item.price_4_day || item.price_per_day || 0} / rental</p>
                    </div>
                    <div className={`w-7 h-7 flex items-center justify-center border flex-shrink-0 transition-all ${
                      isSelected ? "bg-[#9A7B4F] border-[#9A7B4F] text-white" : "border-[#282828]/20 text-[#282828]/40"
                    }`}>
                      {isSelected ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#282828]/10 px-5 py-4 flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 text-[11px] tracking-[0.1em] uppercase border-[#282828]/15 text-[#676664]"
            data-testid="upsell-skip-btn"
          >
            No Thanks
          </Button>
          {newSelections.length > 0 && (
            <Button
              onClick={handleAdd}
              className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] tracking-[0.1em] uppercase"
              data-testid="upsell-add-btn"
            >
              Add {newSelections.length} {newSelections.length === 1 ? "item" : "items"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
